import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Printer, ArrowLeft } from "lucide-react";
import { Loading } from "../../../components/loading";
import { Input } from "../../../components/form";
import { Button } from "../../../components/button";
import { useOrder } from "../hooks";
import { useOrderLines } from "../../order-lines/hooks";
import { SignaturePad } from "../../signatures/components";
import { useSignOrder, useSignatures } from "../../signatures/hooks";
import { formatDisplayDate } from "../../../helpers/date";
import { sha256 } from "../../../helpers/hash";
import { AddressType } from "../../../__generated__/globalTypes";

const COMPANY = {
  name: "DAEMON ENERGY",
  street: "47 RUE VIVIENNE",
  city: "75002 PARIS",
  siren: "104 694 443",
  siret: "104 694 443 00011",
  vat: "FR57 104 694 443",
  iban: "FR7616958000018592770304239",
  bic: "QNTOFRP1XXX",
};

const euros = (cents?: number | null) => `${((cents ?? 0) / 100).toFixed(2)} €`;

export const OrderFormDocument: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading } = useOrder(+id!);
  // Order.lines n'est pas résolu par le serveur : on interroge orderLines
  const { data: linesData, loading: linesLoading } = useOrderLines({
    where: { orderId: +id! },
  });
  const { data: signaturesData } = useSignatures({ where: { orderId: +id! } });
  const { sign, loading: signing } = useSignOrder();

  const [signature, setSignature] = useState<string | null>(null);
  const [signerName, setSignerName] = useState("");
  const [signerRole, setSignerRole] = useState("");

  const order = data?.order?.result;
  const customer = order?.customer;

  const addressOf = (type: AddressType) =>
    customer?.addresses?.find((address) => address.type === type) ??
    customer?.addresses?.find((address) => address.isMain);

  const formatAddress = (address?: {
    streetNumber: string | null;
    street: string | null;
    postal: string | null;
    city: string | null;
  } | null) => {
    if (!address) return "-";
    const line = [address.streetNumber, address.street].filter(Boolean).join(" ");
    const town = [address.postal, address.city].filter(Boolean).join(" ");
    return [line, town].filter(Boolean).join(", ") || "-";
  };

  const mainContact = customer?.contacts?.[0];

  const lines = linesData?.orderLines?.results ?? [];
  const existingSignature = signaturesData?.signatures?.results?.[0];

  const totals = useMemo(() => {
    const ht = lines.reduce((sum, line) => sum + (line.lineTotalCents ?? 0), 0);
    const ttc = lines.reduce(
      (sum, line) =>
        sum + (line.lineTotalCents ?? 0) * (1 + (line.vatRate ?? 0) / 100),
      0
    );
    return { ht, ttc, tva: ttc - ht };
  }, [lines]);

  if (loading || linesLoading) return <Loading />;

  if (!order) {
    return (
      <div className="main-container">
        <div className="card">
          <p className="text-red-600">Commande introuvable</p>
        </div>
      </div>
    );
  }

  const handleSign = async () => {
    if (!signature) {
      toast.error("La signature est obligatoire");
      return;
    }
    if (!signerName.trim()) {
      toast.error("Le nom du signataire est obligatoire");
      return;
    }

    // Empreinte du contenu accepté, pour tracer ce qui a été signé
    const documentHashSha256 = await sha256(
      JSON.stringify({
        orderId: order.id,
        number: order.number,
        customer: customer?.name,
        lines: lines.map((line) => ({
          label: line.frozenLabel,
          quantity: line.quantity,
          unitPriceCents: line.unitPriceCents,
          vatRate: line.vatRate,
          lineTotalCents: line.lineTotalCents,
        })),
        totalTtcCents: Math.round(totals.ttc),
      })
    );

    const ok = await sign({
      orderId: order.id,
      imagePng: signature,
      signerName: signerName.trim(),
      signerRole: signerRole.trim() || "Client",
      signerEmail: customer?.email,
      documentHashSha256,
    });

    if (ok) navigate(`/order/${id}`);
  };

  return (
    <div className="main-container">
      {/* Barre d'actions — masquée à l'impression */}
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button
          type="button"
          onClick={() => navigate(`/order/${id}`)}
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-zinc-300 hover:underline"
        >
          <ArrowLeft size={16} />
          Retour à la commande
        </button>

        <Button variant="secondary" onClick={() => window.print()}>
          <span className="inline-flex items-center gap-2">
            <Printer size={16} />
            Imprimer / PDF
          </span>
        </Button>
      </div>

      <div className="card">
        {/* En-tête émetteur */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-zinc-100">
              BON DE COMMANDE
            </h1>
            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1">
              N° {order.number ?? `#${order.id}`} — {formatDisplayDate(order.createdAt)}
            </p>
          </div>
          <div className="text-sm text-gray-700 dark:text-zinc-300">
            <p className="font-semibold">{COMPANY.name}</p>
            <p>{COMPANY.street}</p>
            <p>{COMPANY.city}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-zinc-800 text-xs text-gray-600 dark:text-zinc-400 space-y-0.5">
          <p>
            <span className="font-semibold">SIREN :</span> {COMPANY.siren}
          </p>
          <p>
            <span className="font-semibold">SIRET du siège social :</span> {COMPANY.siret}
          </p>
          <p>
            <span className="font-semibold">N° TVA intracommunautaire :</span> {COMPANY.vat}
          </p>
          <p>
            <span className="font-semibold">IBAN :</span> {COMPANY.iban}
          </p>
          <p>
            <span className="font-semibold">BIC/SWIFT :</span> {COMPANY.bic}
          </p>
        </div>

        {/* Client / Acheteur */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-zinc-800">
          <h2 className="font-semibold text-gray-900 dark:text-zinc-100 mb-3">
            Client / Acheteur
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700 dark:text-zinc-300">
            <p>
              <span className="text-gray-500 dark:text-zinc-400">Siren / Siret : </span>
              {customer?.siren ?? "-"}
            </p>
            <p>
              <span className="text-gray-500 dark:text-zinc-400">Nom de l'entreprise : </span>
              {customer?.name ?? "-"}
            </p>
            <p>
              <span className="text-gray-500 dark:text-zinc-400">Nom du contact : </span>
              {mainContact
                ? `${mainContact.civility ?? ""} ${mainContact.firstname ?? ""} ${
                    mainContact.lastname ?? ""
                  }`.trim()
                : "-"}
            </p>
            <p>
              <span className="text-gray-500 dark:text-zinc-400">Téléphone : </span>
              {customer?.phone ?? "-"}
            </p>
            <p>
              <span className="text-gray-500 dark:text-zinc-400">Email : </span>
              {customer?.email ?? "-"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-3 text-sm text-gray-700 dark:text-zinc-300">
            <p>
              <span className="text-gray-500 dark:text-zinc-400">
                Adresse de facturation :{" "}
              </span>
              {formatAddress(addressOf(AddressType.Billing))}
            </p>
            <p>
              <span className="text-gray-500 dark:text-zinc-400">
                Adresse de livraison :{" "}
              </span>
              {formatAddress(addressOf(AddressType.Delivery))}
            </p>
          </div>
        </div>

        {/* Récapitulatif des lignes */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-zinc-800">
          <h2 className="font-semibold text-gray-900 dark:text-zinc-100 mb-3">
            Récapitulatif de la commande
          </h2>

          {!lines.length ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              Aucune ligne sur cette commande
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-900">
                    <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-zinc-300">
                      Désignation
                    </th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-700 dark:text-zinc-300">
                      Qté
                    </th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-700 dark:text-zinc-300">
                      P.U. HT
                    </th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-700 dark:text-zinc-300">
                      TVA
                    </th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-700 dark:text-zinc-300">
                      Total HT
                    </th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-700 dark:text-zinc-300">
                      Total TTC
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-zinc-800">
                  {lines.map((line) => (
                    <tr key={`document-line-${line.id}`}>
                      <td className="px-3 py-2 text-gray-900 dark:text-zinc-200">
                        {line.frozenLabel}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-zinc-200">
                        {line.quantity}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-zinc-200">
                        {euros(line.unitPriceCents)}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-zinc-200">
                        {line.vatRate} %
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-zinc-200">
                        {euros(line.lineTotalCents)}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-zinc-200">
                        {euros(
                          Math.round(
                            (line.lineTotalCents ?? 0) * (1 + (line.vatRate ?? 0) / 100)
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Totaux */}
          <div className="flex justify-end mt-4">
            <div className="w-full sm:w-64 text-sm space-y-1">
              <div className="flex justify-between text-gray-700 dark:text-zinc-300">
                <span>Total HT</span>
                <span>{euros(Math.round(totals.ht))}</span>
              </div>
              <div className="flex justify-between text-gray-700 dark:text-zinc-300">
                <span>TVA</span>
                <span>{euros(Math.round(totals.tva))}</span>
              </div>
              {!!order.deliveryFeeCents && (
                <div className="flex justify-between text-gray-700 dark:text-zinc-300">
                  <span>Frais de livraison</span>
                  <span>{euros(order.deliveryFeeCents)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-gray-900 dark:text-zinc-100 pt-1 border-t border-gray-200 dark:border-zinc-800">
                <span>Total TTC</span>
                <span>
                  {euros(Math.round(totals.ttc) + (order.deliveryFeeCents ?? 0))}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Signature */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-zinc-800">
          <h2 className="font-semibold text-gray-900 dark:text-zinc-100 mb-1">
            Signature du client
          </h2>
          <p className="text-xs text-gray-500 dark:text-zinc-400 mb-3">
            Bon pour accord — en signant, le client accepte le contenu et les montants
            de la présente commande.
          </p>

          {existingSignature ? (
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                {existingSignature.signerName}
                {existingSignature.signerRole ? (
                  <span className="font-normal text-gray-500 dark:text-zinc-400">
                    {" "}
                    — {existingSignature.signerRole}
                  </span>
                ) : null}
              </p>
              <p className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5">
                Signé le {formatDisplayDate(existingSignature.signedAt)}
              </p>
              {existingSignature.imagePng && (
                <img
                  src={existingSignature.imagePng}
                  alt={`Signature de ${existingSignature.signerName}`}
                  className="mt-3 max-h-32 bg-white rounded border border-gray-200 dark:border-zinc-700"
                />
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-3 mb-3 print:hidden">
                <Input
                  label="Nom du signataire"
                  className="w-full sm:w-64"
                  value={signerName}
                  onChange={(event) => setSignerName(event.target.value)}
                />
                <Input
                  label="Qualité (optionnel)"
                  className="w-full sm:w-64"
                  placeholder="Gérant, acheteur..."
                  value={signerRole}
                  onChange={(event) => setSignerRole(event.target.value)}
                />
              </div>

              <SignaturePad onChange={setSignature} />

              <div className="mt-4 print:hidden">
                <Button onClick={handleSign} loading={signing} disabled={signing}>
                  Valider la signature
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
